import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { websiteService } from "../../services/websiteService";
import { useSiteContent } from "../siteContent";
import { ChoiceButton, Field, Notice, fieldClass } from "../components/fields";

const emptyForm = {
  pickup: "",
  delivery: "",
  description: "",
  packages: "",
  weight: "",
  date: "",
  name: "",
  mobile: "",
  email: "",
  companyName: "",
  notes: "",
};

export default function ServiceEnquiry() {
  const { company, serviceNeeds } = useSiteContent();
  const [searchParams] = useSearchParams();
  const branch = searchParams.get("branch") || "";
  const [needs, setNeeds] = useState([]);
  const [form, setForm] = useState({
    ...emptyForm,
    notes: branch ? `Enquiry for ${branch}.` : "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleNeed = (need) => {
    setNeeds((current) => (current.includes(need) ? current.filter((item) => item !== need) : [...current, need]));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (needs.length === 0) return;
    setError("");
    setSending(true);
    try {
      const response = await websiteService.submitEnquiry({ ...form, companyName: form.companyName, needs });
      setSubmitted(response.data?.data || { reference: "" });
    } catch (err) {
      setError(err.response?.data?.error || "We could not send that enquiry. Please call the helpline.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0B6A34]">
      <section className="container-page py-14 text-white sm:py-16">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b7e7a8]">Service enquiry</p>
        <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-5xl">Tell us what you need to send</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
          Share the route and the consignment. A branch will call you to confirm the details and the charges before anything is booked.
        </p>
      </section>

      <section className="container-page grid gap-6 pb-16 lg:grid-cols-[1fr_280px]">
        {submitted ? (
          <Notice>
            <p className="font-semibold">Thanks, {form.name}. Your enquiry is with Essaar.</p>
            <p className="mt-2">
              Reference {submitted.reference}. A branch will call you about the route from {form.pickup} to {form.delivery}. You can also call {company.phone}.
            </p>
          </Notice>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-xl bg-white p-5 sm:p-6">
            <fieldset>
              <legend className="text-base font-bold text-[#16301f]">1. What do you need?</legend>
              <p className="mt-1 text-sm text-slate-500">Choose all that apply.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {serviceNeeds.map((need) => (
                  <ChoiceButton key={need} selected={needs.includes(need)} onClick={() => toggleNeed(need)}>
                    {need}
                  </ChoiceButton>
                ))}
              </div>
              {needs.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">Select at least one before sending.</p>
              )}
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">2. Where is it going?</legend>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Pickup city or pincode">
                  <input name="pickup" required value={form.pickup} onChange={update} placeholder="Where we collect from" className={fieldClass} />
                </Field>
                <Field label="Delivery city or pincode">
                  <input name="delivery" required value={form.delivery} onChange={update} placeholder="Where it needs to reach" className={fieldClass} />
                </Field>
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">3. What are you sending?</legend>
              <div className="mt-3 grid gap-4">
                <Field label="Description of goods">
                  <input name="description" required value={form.description} onChange={update} placeholder="For example, 12 cartons of garments" className={fieldClass} />
                </Field>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Number of packages">
                    <input name="packages" required inputMode="numeric" value={form.packages} onChange={update} placeholder="0" className={fieldClass} />
                  </Field>
                  <Field label="Approx. weight (kg)">
                    <input name="weight" required inputMode="decimal" value={form.weight} onChange={update} placeholder="0" className={fieldClass} />
                  </Field>
                  <Field label="Preferred date">
                    <input name="date" required type="date" value={form.date} onChange={update} className={fieldClass} />
                  </Field>
                </div>
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="text-base font-bold text-[#16301f]">4. How do we reach you?</legend>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Your name">
                  <input name="name" required value={form.name} onChange={update} placeholder="Full name" className={fieldClass} />
                </Field>
                <Field label="Mobile number">
                  <input name="mobile" required inputMode="tel" value={form.mobile} onChange={update} placeholder="10-digit mobile" className={fieldClass} />
                </Field>
                <Field label="Email (optional)">
                  <input name="email" type="email" value={form.email} onChange={update} placeholder="name@company.com" className={fieldClass} />
                </Field>
                <Field label="Company / GSTIN (optional)">
                  <input name="companyName" value={form.companyName} onChange={update} placeholder="Company or GSTIN" className={fieldClass} />
                </Field>
                <Field label="Anything else we should know" className="sm:col-span-2">
                  <textarea name="notes" rows={4} value={form.notes} onChange={update} placeholder="Fragile item, pickup timing, special handling" className={fieldClass} />
                </Field>
              </div>
            </fieldset>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={needs.length === 0 || sending}
                className="rounded-md bg-[#0B6A34] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#085628] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send enquiry"}
              </button>
              <p className="text-xs text-slate-500">We use your details only to respond to this enquiry.</p>
            </div>
          </form>
        )}

        <aside className="space-y-4">
          <div className="rounded-xl bg-[#e7f3ea] p-5 text-[#16301f]">
            <h2 className="text-base font-bold">What happens next</h2>
            <ol className="mt-3 space-y-3 text-sm leading-relaxed">
              <li><span className="font-bold text-[#0B6A34]">1</span> Your enquiry reaches the branch nearest to your pickup point.</li>
              <li><span className="font-bold text-[#0B6A34]">2</span> They call you to confirm the goods, route and charges.</li>
              <li><span className="font-bold text-[#0B6A34]">3</span> You book, receive your LR and tracking QR, and your consignment moves.</li>
            </ol>
          </div>
          <div className="rounded-xl border border-white/20 p-5 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#b7e7a8]">Prefer to talk?</p>
            <a href={company.phoneHref} className="mt-2 block text-2xl font-bold">{company.phone}</a>
            <p className="mt-1 text-sm text-white/80">Cargo helpline · {company.email}</p>
            <Link to="/branches" className="mt-3 inline-block text-sm font-semibold text-[#b7e7a8] hover:underline">
              Find a branch near you
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
